// src/pages/AddTrade.js (or src/components/AddTrade.js)
// ✅ Full drop-in file
// ✅ Strict cash-only UI mode kept intact
// ✅ Lean AI pre-moderation before upload/post
// ✅ Retry-safe sponsored payment handling
// ✅ Auto-resume sponsored payment if page refreshes
// ✅ Uses callable: preModerateTradeListing
// ✅ Uses callable: createSponsoredPostPayment
// ✅ Inline requirements checklist and field-level validation hints

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { FaTimes, FaCloudUploadAlt, FaMapMarkerAlt } from "react-icons/fa";
import { serverTimestamp, getDoc, doc, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, storage, functions } from "../firebase";
import { addTradeListing } from "../utils/firestoreTrades";
import { useAuth } from "../contexts/AuthContext";
import { usePopup } from "../contexts/PopupContext";
import "./PostTrade.css";

/* =========================================================
   STRICT CASH-ONLY UI FLAGS
========================================================= */
const CASH_ONLY_UI = true;
const ENABLE_SWAP_OFFER_INTENT_FLOW = false;

/* -------------------- CONFIG -------------------- */
const MAX_IMAGES = 8;
const MAX_MB_PER_IMAGE = 8;
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

const COMPRESS_MAX_W = 1920;
const COMPRESS_MAX_H = 1920;
const COMPRESS_QUALITY = 0.78;
const UPLOAD_CONCURRENCY = 3;

/* Resume pending sponsored payment within 30 minutes */
const SPONSORED_PAYMENT_RESUME_KEY = "pendingSponsoredPayment";
const SPONSORED_PAYMENT_RESUME_TTL_MS = 30 * 60 * 1000;

const PROMO_DAYS = {
  HIGHLIGHT: 21,
  URGENT: 7,
};

const TOP_AD_PRICES = {
  7: 30,
  15: 70,
  31: 90,
};

const PROMO_PRICES = {
  HIGHLIGHT: 70,
  URGENT: 25,
};

const CATEGORIES = [
  "Electronics",
  "Clothing",
  "Home & Garden",
  "Vehicles",
  "Gaming",
  "Books & Media",
  "Jobs & Services",
  "Health & Fitness",
  "Art & Collectibles",
  "Musical Instruments",
  "Sports & Outdoors",
  "Food & Dining",
  "Travel & Experiences",
  "Baby & Kids",
  "Business & Industrial",
  "Pets & Animals",
];

const CONDITIONS = ["Brand New", "Like New", "Good", "Fair", "Needs repair"];

/* -------------------- HELPERS -------------------- */
function normStr(v) {
  return String(v || "").trim();
}

function normEmail(v) {
  return String(v || "").trim().toLowerCase();
}

function toInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.round(n));
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function validateFiles(files) {
  const errors = [];
  if (files.length > MAX_IMAGES) errors.push(`Please upload up to ${MAX_IMAGES} images.`);
  for (const f of files) {
    if (!ALLOWED_TYPES.has(f.type)) {
      errors.push(`Unsupported file type: ${f.name}. Use JPG/PNG/WebP.`);
    }
    const mb = f.size / (1024 * 1024);
    if (mb > MAX_MB_PER_IMAGE) {
      errors.push(`File too large: ${f.name}. Max ${MAX_MB_PER_IMAGE}MB.`);
    }
  }
  return errors;
}

function safeFileName(name) {
  return String(name || "image").replace(/[^\w.\-]+/g, "_");
}

function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

async function compressImageFile(
  file,
  { maxW = COMPRESS_MAX_W, maxH = COMPRESS_MAX_H, quality = COMPRESS_QUALITY } = {}
) {
  const img = await fileToImage(file);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;

  const ratio = Math.min(maxW / srcW, maxH / srcH, 1);
  const dstW = Math.max(1, Math.round(srcW * ratio));
  const dstH = Math.max(1, Math.round(srcH * ratio));

  const canvas = document.createElement("canvas");
  canvas.width = dstW;
  canvas.height = dstH;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported.");

  ctx.drawImage(img, 0, 0, dstW, dstH);

  const outType = "image/jpeg";
  const q = clamp(quality, 0.4, 0.92);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, outType, q));
  if (!blob) throw new Error("Image compression failed.");

  const base = file.name.replace(/\.[^/.]+$/, "");
  const compressedName = `${base}-compressed.jpg`;
  const compressedFile = new File([blob], compressedName, { type: outType });

  return { file: compressedFile };
}

async function fileToModerationDataUrl(file, { maxW = 768, maxH = 768, quality = 0.7 } = {}) {
  const img = await fileToImage(file);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;

  const ratio = Math.min(maxW / srcW, maxH / srcH, 1);
  const dstW = Math.max(1, Math.round(srcW * ratio));
  const dstH = Math.max(1, Math.round(srcH * ratio));

  const canvas = document.createElement("canvas");
  canvas.width = dstW;
  canvas.height = dstH;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported.");

  ctx.drawImage(img, 0, 0, dstW, dstH);
  return canvas.toDataURL("image/jpeg", quality);
}

function uploadOneFileWithProgress({ file, path, onProgress }) {
  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);

    task.on(
      "state_changed",
      (snap) => {
        if (typeof onProgress === "function") onProgress(snap.bytesTransferred, snap.totalBytes);
      },
      (err) => reject(err),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve(url);
        } catch (e) {
          reject(e);
        }
      }
    );
  });
}

function friendlySponsoredPaymentError(err) {
  const msg = String(err?.message || "").toLowerCase();

  if (msg.includes("already confirmed")) {
    return "This listing's sponsored payment has already been confirmed.";
  }
  if (msg.includes("no paid promotions selected")) {
    return "No paid promotion option was selected for this listing.";
  }
  if (msg.includes("payment session")) {
    return "Your listing was posted, but the sponsored payment session could not be created. Please retry from the listing.";
  }

  return "Your listing was posted, but we could not start the sponsored payment. Please retry from the listing.";
}

async function runWithConcurrency(tasks, limit) {
  const results = new Array(tasks.length);
  let idx = 0;

  const workers = new Array(Math.min(limit, tasks.length)).fill(0).map(async () => {
    while (true) {
      const myIdx = idx++;
      if (myIdx >= tasks.length) break;
      results[myIdx] = await tasks[myIdx]();
    }
  });

  await Promise.all(workers);
  return results;
}

const addDaysTs = (days) => {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Timestamp.fromDate(new Date(Date.now() + n * 24 * 60 * 60 * 1000));
};

function calcSponsoredTotal(promotions = {}) {
  const topAdDays = Number(promotions.topAdDays || 0);

  return (
    (TOP_AD_PRICES[topAdDays] || 0) +
    (promotions.highlight ? PROMO_PRICES.HIGHLIGHT : 0) +
    (promotions.urgent ? PROMO_PRICES.URGENT : 0)
  );
}

/* -------------------- COMPONENT -------------------- */
const AddTrade = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { showPopup } = usePopup();

  const offerIntent = location.state?.offerIntent || null;
  const isOfferFlow = !!offerIntent?.requestedTradeId;

  const [requestedMeta, setRequestedMeta] = useState(() => {
    const t = offerIntent?.requestedTrade || null;
    return {
      requestedTradeId: normStr(offerIntent?.requestedTradeId),
      requestedOwnerId: normStr(offerIntent?.requestedOwnerId),
      requestedOwnerEmail: normEmail(offerIntent?.requestedOwnerEmail),
      requestedTitle: normStr(offerIntent?.requestedTitle),
      requestedImage: normStr(t?.imageURL || t?.imageUrls?.[0] || t?.imageURLs?.[0] || ""),
      requestedCity: normStr(t?.location?.city || ""),
      requestedCountry: normStr(t?.location?.country || ""),
    };
  });

  const forceRenderKey = useMemo(() => {
    return typeof location.state?.postKey === "number" ? location.state.postKey : "default-form";
  }, [location.state]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    condition: "",
    locationText: "",
    tradePreference: CASH_ONLY_UI ? "cash_only" : "trade_or_cash",
    expectedCashAmount: "",
    minimumCashAmount: "",
    promotions: { topAd: null, highlight: false, urgent: false },
  });

  const [geo, setGeo] = useState({ latitude: null, longitude: null, country: "", city: "" });
  const [locError, setLocError] = useState("");

  const [imageFiles, setImageFiles] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const previewRef = useRef([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [moderationBusy, setModerationBusy] = useState(false);
  const [sponsoredPaymentBusy, setSponsoredPaymentBusy] = useState(false);
  const [resumeTradeId, setResumeTradeId] = useState("");
  const [progress, setProgress] = useState({
    totalBytes: 0,
    uploadedBytes: 0,
    percent: 0,
    perFile: {},
  });

  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const titleRef = useRef(null);
  const categoryRef = useRef(null);
  const descriptionRef = useRef(null);
  const conditionRef = useRef(null);
  const locationRef = useRef(null);
  const priceRef = useRef(null);
  const imagesRef = useRef(null);

  const preModerateTradeListing = useMemo(
    () => httpsCallable(functions, "preModerateTradeListing"),
    []
  );

  const createSponsoredPostPayment = useMemo(
    () => httpsCallable(functions, "createSponsoredPostPayment"),
    []
  );

  useEffect(() => {
    if (!currentUser) {
      navigate("/login", { replace: true, state: { from: "/market/post-trade" } });
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!CASH_ONLY_UI) return;
    if (!isOfferFlow) return;

    showPopup({
      type: "error",
      title: "Swaps unavailable",
      message:
        "Swaps are temporarily disabled. You can still post your item normally and make a cash offer from the listing.",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOfferFlow]);

  useEffect(() => {
    const hydrateRequestedMeta = async () => {
      if (!isOfferFlow) return;
      const reqTradeId = normStr(offerIntent?.requestedTradeId);
      if (!reqTradeId) return;

      const needsFetch =
        !requestedMeta.requestedOwnerId ||
        !requestedMeta.requestedTitle ||
        !requestedMeta.requestedImage ||
        !requestedMeta.requestedCountry;

      if (!needsFetch) return;

      try {
        const snap = await getDoc(doc(db, "Trades", reqTradeId));
        if (!snap.exists()) return;
        const d = snap.data() || {};

        setRequestedMeta((prev) => ({
          ...prev,
          requestedTradeId: reqTradeId,
          requestedOwnerId: prev.requestedOwnerId || normStr(d.ownerId || d.ownerUid),
          requestedOwnerEmail: prev.requestedOwnerEmail || normEmail(d.ownerEmail),
          requestedTitle: prev.requestedTitle || normStr(d.title),
          requestedImage:
            prev.requestedImage ||
            normStr(d.imageURLs?.[0] || d.imageUrl?.[0] || d.imageURL || d.images?.[0] || ""),
          requestedCity: prev.requestedCity || normStr(d.location?.city),
          requestedCountry: prev.requestedCountry || normStr(d.location?.country),
        }));
      } catch (e) {
        console.warn("Could not hydrate requested trade meta:", e);
      }
    };

    hydrateRequestedMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOfferFlow, offerIntent?.requestedTradeId]);

  const reverseLookup = useCallback(async (latitude, longitude) => {
    try {
      setLocError("");
      const res = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      );
      if (!res.ok) throw new Error("Reverse geocode failed");
      const data = await res.json();

      const city = data.city || data.locality || "";
      const country = data.countryName || "";

      setGeo({ latitude, longitude, country, city });

      setForm((prev) => {
        if (normStr(prev.locationText)) return prev;
        return { ...prev, locationText: city || prev.locationText };
      });
    } catch {
      setLocError("Could not detect location. Please type your city/suburb.");
    }
  }, []);

  const requestLocation = useCallback(() => {
    setLocError("");
    if (!navigator.geolocation) {
      setLocError("Geolocation is not supported on this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => reverseLookup(pos.coords.latitude, pos.coords.longitude),
      (err) => setLocError(err?.message || "Location permission was blocked."),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [reverseLookup]);

  useEffect(() => {
    requestLocation();
}, [requestLocation]);

const topAdDays = Number(form.promotions.topAd || 0);
const hasTopAd = topAdDays > 0;
const hasHighlight = !!form.promotions.highlight;
const hasUrgent = !!form.promotions.urgent;

const topAdUntil = hasTopAd ? addDaysTs(topAdDays) : null;
const highlightUntil = hasHighlight ? addDaysTs(PROMO_DAYS.HIGHLIGHT) : null;
const urgentUntil = hasUrgent ? addDaysTs(PROMO_DAYS.URGENT) : null;

const promotions = {
    // Top Ad / sponsored placement
    topAd: hasTopAd ? topAdDays : 0,
    topAdEnabled: hasTopAd,
    topAdDays: hasTopAd ? topAdDays : 0,
    topAdUntil,

    // Compatibility aliases for marketplace display logic
    sponsored: hasTopAd,
    sponsoredDays: hasTopAd ? topAdDays : 0,
    sponsoredUntil: topAdUntil,

    // Highlight extra
    highlight: hasHighlight,
    highlightDays: hasHighlight ? PROMO_DAYS.HIGHLIGHT : 0,
    highlightUntil,

    // Urgent extra
    urgent: hasUrgent,
    urgentDays: hasUrgent ? PROMO_DAYS.URGENT : 0,
    urgentUntil,

    // Helpful for future filtering/reporting
    promotionTypes: [
      hasTopAd ? "top_ad" : null,
      hasHighlight ? "highlight" : null,
      hasUrgent ? "urgent" : null,
    ].filter(Boolean),

    // This makes the intention explicit:
    // every open listing remains normal, while paid selections add extra surfaces.
    activeSurfaces: [
      "normal",
      hasTopAd ? "sponsored" : null,
      hasHighlight ? "highlighted" : null,
      hasUrgent ? "urgent" : null,
    ].filter(Boolean),
  };

  const validation = useMemo(() => {
    const title = normStr(form.title);
    const description = normStr(form.description);
    const category = normStr(form.category);
    const condition = normStr(form.condition);
    const price = toInt(form.expectedCashAmount);
    const minPrice = toInt(form.minimumCashAmount);

    const hasManualLoc = !!normStr(form.locationText);
    const hasDetectedLoc = !!normStr(geo.city) || (geo.latitude != null && geo.longitude != null);
    const hasLocation = hasManualLoc || hasDetectedLoc;

    const items = [
      {
        key: "title",
        label: "Item name",
        refKey: "title",
        done: title.length >= 3,
        message: "Enter an item name with at least 3 characters.",
        success: "Item name looks good.",
      },
      {
        key: "category",
        label: "Category",
        refKey: "category",
        done: !!category,
        message: "Select the category that best matches your item.",
        success: "Category selected.",
      },
      {
        key: "description",
        label: "Description",
        refKey: "description",
        done: description.length >= 10,
        message: "Add a description with at least 10 characters. Mention condition, defects, and accessories.",
        success: "Description is detailed enough.",
      },
      {
        key: "condition",
        label: "Condition",
        refKey: "condition",
        done: !!condition,
        message: "Select the item condition.",
        success: "Condition selected.",
      },
      {
        key: "location",
        label: "Location",
        refKey: "location",
        done: hasLocation,
        message: "Add your city/suburb or use detected location.",
        success: "Location is ready.",
      },
      {
        key: "price",
        label: "Price",
        refKey: "price",
        done: price > 0 && (minPrice === 0 || minPrice <= price),
        message: minPrice > 0 && minPrice > price
          ? "Minimum amount cannot be higher than the expected price."
          : "Enter a cash price greater than R0.",
        success: "Price is ready.",
      },
      {
        key: "minPrice",
        label: "Minimum Price",
        refKey: "minPrice",
        done: minPrice >= 0 && minPrice <= price,
        message: minPrice < 0
          ? "Minimum amount cannot be negative."
          : minPrice > price
          ? "Minimum amount cannot be higher than the expected price."
          : "Optional: Enter a minimum cash amount (leave blank for no minimum).",
        success: "Minimum price is set.",
      },
      {
        key: "images",
        label: "Images",
        refKey: "images",
        done: imageFiles.length >= 1 && imageFiles.length <= MAX_IMAGES,
        message:
          imageFiles.length > MAX_IMAGES
            ? `Upload no more than ${MAX_IMAGES} images.`
            : `Upload at least 1 clear image. You can upload up to ${MAX_IMAGES}.`,
        success: `${imageFiles.length} image${imageFiles.length === 1 ? "" : "s"} selected.`,
      },
    ];

    const missing = items.filter((x) => !x.done);

    return {
      items,
      missing,
      firstMissing: missing[0] || null,
      isValid: missing.length === 0,
      byKey: items.reduce((acc, item) => {
        acc[item.key] = item;
        return acc;
      }, {}),
    };
  }, [form, imageFiles.length, geo.city, geo.latitude, geo.longitude]);

  const canSubmit = validation.isValid;

  const fieldIssueClass = (key) => {
    const item = validation.byKey[key];
    if (!item) return "";
    if (item.done) return "pt-field--ok";
    return attemptedSubmit ? "pt-field--issue" : "";
  };

  const renderRequirementHint = (key) => {
    const item = validation.byKey[key];
    if (!item) return null;

    const showIssue = attemptedSubmit && !item.done;

    return (
      <div className={`pt-reqHint ${item.done ? "is-ok" : showIssue ? "is-issue" : ""}`}>
        <span>{item.done ? "✓" : showIssue ? "!" : "•"}</span>
        <span>{item.done ? item.success : item.message}</span>
      </div>
    );
  };

  const scrollToFirstMissing = () => {
    const refMap = {
      title: titleRef,
      category: categoryRef,
      description: descriptionRef,
      condition: conditionRef,
      location: locationRef,
      price: priceRef,
      images: imagesRef,
    };

    const ref = refMap[validation.firstMissing?.refKey];

    try {
      ref?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {}

    try {
      const input = ref?.current?.querySelector?.("input, textarea, select, button");
      input?.focus?.();
    } catch {}
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));
  const setPromo = (name, value) => {
    setForm((prev) => {
      const newPromos = { ...prev.promotions, [name]: value };
      // If Top Ad is deselected, clear extras since they require Top Ad
      if (name === "topAd" && !value) {
        newPromos.highlight = false;
        newPromos.urgent = false;
      }
      return { ...prev, promotions: newPromos };
    });
  };

  const handleSelectAllPromos = (checked) => {
    setForm((prev) => ({
      ...prev,
      promotions: { topAd: checked ? "31" : null, highlight: checked, urgent: checked },
    }));
  };

  const Popup = ({
  open,
  type = "success",
  title = "",
  message,
  actionLabel = "",
  actionHref = "",
  onClose,
  autoHide = true,
  autoHideMs = 2200,
}) => {
  useEffect(() => {
    if (!open || !autoHide) return;
    const t = setTimeout(() => {
      onClose?.();

      if (actionHref) {
        navigate(actionHref);
      }
    }, autoHideMs);

    return () => clearTimeout(t);
  
  }, [open, autoHide, autoHideMs, onClose, actionHref, navigate]);

  if (!open) return null;

  
  const isError = type === "error";
  const isInfo = type === "info";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15, 23, 42, 0.42)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#fff",
          borderRadius: 20,
          padding: 22,
          boxShadow: "0 24px 60px rgba(0,0,0,0.20)",
          border: isError
            ? "1px solid rgba(220, 38, 38, 0.22)"
            : "1px solid rgba(16, 185, 129, 0.22)",
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 14,
            background: isError
            ? "rgba(220, 38, 38, 0.10)"
            : isInfo
            ? "rgba(59, 130, 246, 0.10)"
            : "rgba(16, 185, 129, 0.10)",
          color: isError
            ? "#dc2626"
            : isInfo
            ? "#2563eb"
            : "#10b981",
            fontSize: 24,
            fontWeight: 900,
          }}
        >
          
          {isError ? "!" : isInfo ? "…" : "✓"}
        </div>

        <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
          {title || (isError ? "Action needed" : "Trade posted")}
        </div>

        <div style={{ fontSize: 14, lineHeight: 1.5, color: "#475569" }}>
          {message}
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 18,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "1px solid rgba(15,23,42,0.10)",
              background: "#fff",
              color: "#0f172a",
              padding: "12px 18px",
              borderRadius: 12,
              fontWeight: 700,
              cursor: "pointer",
              minHeight: 44,
            }}
          >
            Close
          </button>

          {actionHref ? (
            <button
              type="button"
              onClick={() => {
                onClose?.();
                navigate(actionHref);
              }}
              style={{
                border: "none",
                background: "#111827",
                color: "#fff",
                padding: "12px 18px",
                borderRadius: 12,
                fontWeight: 700,
                cursor: "pointer",
                minHeight: 44,
              }}
            >
              {actionLabel || "Open listing"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

  function friendlyError(err) {
    const code = String(err?.code || "");
    const msg = String(err?.message || "").toLowerCase();

    if (code.includes("storage/unauthorized") || msg.includes("unauthorized")) {
      return "We could not upload your images. Please sign in again and try.";
    }
    if (code.includes("permission-denied") || msg.includes("insufficient permissions")) {
      return "You do not have permission to post this trade. Please sign in again and try.";
    }
    if (msg.includes("network") || msg.includes("failed to fetch")) {
      return "Network issue detected. Please check your connection and try again.";
    }
    return "Something went wrong while listing your trade. Please try again.";
  }

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const errs = validateFiles(files);
    if (errs.length) {
      setImageFiles([]);
      setUploadFiles([]);

      previewRef.current.forEach((u) => URL.revokeObjectURL(u));
      previewRef.current = [];
      setPreviewImages([]);

      showPopup({
        type: "error",
        title: "Upload issue",
        message: errs[0],
      });
      return;
    }

    previewRef.current.forEach((u) => URL.revokeObjectURL(u));
    previewRef.current = [];
    const previews = files.map((file) => URL.createObjectURL(file));
    previewRef.current = previews;
    setPreviewImages(previews);

    setImageFiles(files);

    try {
     
     showPopup({
        type: "info",
        title: "Preparing images",
        message: "Optimising images for faster upload…",
      });
      const compressed = [];
      for (const f of files) {
        const out = await compressImageFile(f);
        compressed.push(out.file);
      }
      setUploadFiles(compressed);
    } catch (err) {
      console.error("Compression failed, falling back to originals:", err);
      setUploadFiles(files);
     showPopup({
        type: "error",
        title: "Image optimisation issue",
        message: "Could not optimise images. Uploading originals instead.",
      });
    }
  };

  useEffect(() => {
    return () => {
      previewRef.current.forEach((u) => URL.revokeObjectURL(u));
      previewRef.current = [];
    };
  }, []);

  const close = () => {
    navigate("/", { state: { refresh: true } });
    window.scrollTo(0, 0);
  };

  const resetProgress = () => {
    setProgress({ totalBytes: 0, uploadedBytes: 0, percent: 0, perFile: {} });
  };

  const clearPendingSponsoredPayment = useCallback(() => {
    try {
      sessionStorage.removeItem(SPONSORED_PAYMENT_RESUME_KEY);
    } catch {}
  }, []);

  const launchSponsoredPayment = useCallback(
    async (tradeId) => {
      const safeTradeId = normStr(tradeId);
      if (!safeTradeId) {
        throw new Error("Missing tradeId for sponsored payment.");
      }

      setSponsoredPaymentBusy(true);
      try {
        const resp = await createSponsoredPostPayment({ tradeId: safeTradeId });
        const payment = resp?.data || {};

        console.log("[AddTrade] sponsored payment response:", payment);

        if (payment?.alreadyPaid) {
          clearPendingSponsoredPayment();
          
          showPopup({
            type: "success",
            title: "Payment already confirmed",
            message: "Sponsored payment was already confirmed for this listing.",
          });
          return { ok: true, alreadyPaid: true };
        }

        const endpoint = payment?.payfastUrl;
        const fields = payment?.fields || {};
        const redirectUrl = payment?.redirectUrl || "";

        if (!endpoint || !fields.m_payment_id) {
          throw new Error(
            payment?.message ||
              payment?.error ||
              "Sponsored payment session could not be created."
          );
        }

        try {
          sessionStorage.setItem(
            SPONSORED_PAYMENT_RESUME_KEY,
            JSON.stringify({
              tradeId: safeTradeId,
              mPaymentId: String(fields.m_payment_id || ""),
              at: Date.now(),
            })
          );
        } catch {}

        if (redirectUrl) {
          window.location.href = redirectUrl;
          return { ok: true, redirected: true };
        }

        const pfForm = document.createElement("form");
        pfForm.method = "POST";
        pfForm.action = endpoint;
        pfForm.style.display = "none";

        Object.entries(fields).forEach(([key, value]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = String(value ?? "");
          pfForm.appendChild(input);
        });

        document.body.appendChild(pfForm);
        pfForm.submit();

        return { ok: true, redirected: true };
      } finally {
        setSponsoredPaymentBusy(false);
      }
    },
    [createSponsoredPostPayment, clearPendingSponsoredPayment]
  );

  useEffect(() => {
    let cancelled = false;

    const tryResumeSponsoredPayment = async () => {
      try {
        const raw = sessionStorage.getItem(SPONSORED_PAYMENT_RESUME_KEY);
        if (!raw) return;

        const parsed = JSON.parse(raw || "{}");
        const tradeId = normStr(parsed?.tradeId);
        const startedAt = Number(parsed?.at || 0);

        if (!tradeId) {
          sessionStorage.removeItem(SPONSORED_PAYMENT_RESUME_KEY);
          return;
        }

        if (!startedAt || Date.now() - startedAt > SPONSORED_PAYMENT_RESUME_TTL_MS) {
          sessionStorage.removeItem(SPONSORED_PAYMENT_RESUME_KEY);
          return;
        }

        if (cancelled) return;

        setResumeTradeId(tradeId);
        
        showPopup({
          type: "success",
          title: "Resuming payment",
          message: "We found a pending sponsored payment for your listing. Resuming payment...",
        });

        await launchSponsoredPayment(tradeId);
      } catch (err) {
        console.error("Failed to auto-resume sponsored payment:", err);
      }
    };

    tryResumeSponsoredPayment();

    return () => {
      cancelled = true;
    };
  }, [launchSponsoredPayment]);

  const runPreModerationCheck = useCallback(async () => {
    const filesForCheck = (uploadFiles.length ? uploadFiles : imageFiles).slice(0, 1);

    const imageDataUrls = [];
    for (const file of filesForCheck) {
      imageDataUrls.push(await fileToModerationDataUrl(file));
    }

    const res = await preModerateTradeListing({
      title: normStr(form.title),
      description: normStr(form.description),
      category: normStr(form.category),
      expectedCashAmount: toInt(form.expectedCashAmount),
      minimumCashAmount: toInt(form.minimumCashAmount),
      images: imageDataUrls,
    });

    return (
      res?.data || {
        decision: "allow",
        message: "",
        moderationRoute: "unknown",
        fallbackAction: "post_pending_review",
        categoryRisk: "medium",
        sellerTrust: "unknown",
      }
    );
  }, [
    preModerateTradeListing,
    form.title,
    form.description,
    form.category,
    form.expectedCashAmount,
    form.minimumCashAmount,
    uploadFiles,
    imageFiles,
  ]);

  const handleSubmit = async (e) => {
    let redirectingToPayFast = false;
    e.preventDefault();
    setAttemptedSubmit(true);

    if (isSubmitting || sponsoredPaymentBusy) return;

    if (!currentUser?.uid) {
      
      showPopup({
        type: "error",
        title: "Action needed",
        message: "Please sign in again to post a trade.",
        autoHide: true,
      });
      return;
    }

    if (!canSubmit) {
      scrollToFirstMissing();

      showPopup({
        type: "error",
        title: "Almost there",
        message:
          validation.firstMissing?.message ||
          "Please complete the highlighted fields before posting.",
      });
      return;
    }

    const title = normStr(form.title);
    const description = normStr(form.description);

    if (title.length < 3) {
    showPopup({
      type: "error",
      title: "Title too short",
      message: "Title is too short.",
    });
      return;
    }

    if (description.length < 10) {
      showPopup({
        type: "error",
        title: "Description too short",
        message: "Description is too short.",
      });
      return;
    }
    

    let moderationDecision = "allow";
    let moderationMessage = "";
    let moderationRoute = "unknown";
    let categoryRisk = "medium";
    let sellerTrust = "unknown";
    let postAsPendingReview = false;

    setModerationBusy(true);
    try {
      const moderation = await runPreModerationCheck();

      moderationDecision = moderation?.decision || "allow";
      moderationMessage = moderation?.message || "";
      moderationRoute = moderation?.moderationRoute || "unknown";
      categoryRisk = moderation?.categoryRisk || "medium";
      sellerTrust = moderation?.sellerTrust || "unknown";

      const fallbackAction = moderation?.fallbackAction || "post_pending_review";

      if (moderationDecision === "block") {
        showPopup({
          type: "error",
          title: "Listing blocked",
          message:
            moderationMessage ||
            "One or more images or text entries are not suitable for a marketplace listing.",
          autoHide: false,
        });
        return;
      }

      if (moderationDecision === "warn") {
        showPopup({
        type: "error",
        title: "Please revise listing",
        message:
          moderationMessage ||
          "Please use clear product photos and neutral item descriptions.",
        autoHide: false,
      });
        return;
      }

      if (fallbackAction === "post_pending_review") {
        postAsPendingReview = true;
      } else if (fallbackAction === "post_open") {
        postAsPendingReview = false;
      } else {
        postAsPendingReview = true;
      }

      console.log("[AddTrade] moderationDecision:", moderationDecision);
      console.log("[AddTrade] moderationRoute:", moderationRoute);
      console.log("[AddTrade] categoryRisk:", categoryRisk);
      console.log("[AddTrade] postAsPendingReview:", postAsPendingReview);
      console.log("[AddTrade] sellerTrust:", sellerTrust);
    } catch (err) {
      console.error("Pre-moderation failed:", err);

      moderationDecision = "pending_review";
      moderationMessage =
        "We could not complete automated moderation. Your listing will be submitted for manual review.";
      moderationRoute = "client_fallback";
      categoryRisk = normStr(form.category) ? "medium" : "unknown";
      postAsPendingReview = true;
    } finally {
      setModerationBusy(false);
    }

    setIsSubmitting(true);
    resetProgress();

    try {
      const tradeId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

      const filesToUpload = uploadFiles.length ? uploadFiles : imageFiles;
      const totalBytes = filesToUpload.reduce((sum, f) => sum + (f?.size || 0), 0);
      setProgress((p) => ({ ...p, totalBytes }));

      const perFile = {};
      let uploadedBytesTotal = 0;

      const tasks = filesToUpload.map((file, i) => async () => {
        const safeName = safeFileName(file.name);
        const path = `trade-images/${currentUser.uid}/${tradeId}/${Date.now()}-${safeName}`;

        perFile[i] = { sent: 0, total: file.size || 0 };

        const url = await uploadOneFileWithProgress({
          file,
          path,
          onProgress: (sent, total) => {
            const prevSent = perFile[i]?.sent || 0;
            const delta = Math.max(0, sent - prevSent);

            perFile[i] = { sent, total };
            uploadedBytesTotal += delta;

            const pct =
              totalBytes > 0 ? Math.round((uploadedBytesTotal / totalBytes) * 100) : 0;

            setProgress({
              totalBytes,
              uploadedBytes: uploadedBytesTotal,
              percent: clamp(pct, 0, 100),
              perFile: { ...perFile },
            });
          },
        });

        return url;
      });

      const imageURLs = await runWithConcurrency(tasks, UPLOAD_CONCURRENCY);

      const expectedCashAmount = toInt(form.expectedCashAmount);
      const minimumCashAmount = toInt(form.minimumCashAmount);

      const newTrade = {
        title,
        description,
        category: form.category,
        condition: form.condition,
        tradePreference: CASH_ONLY_UI ? "cash_only" : form.tradePreference,
        expectedCashAmount,
        minimumCashAmount,

        promotions,

        // Compatibility fields for listing surfaces.
        // These prevent the promotion from becoming exclusive to one section only.
        promotionTypes: promotions.promotionTypes,
        activeSurfaces: promotions.activeSurfaces,

        isTopAd: promotions.topAdEnabled,
        topAd: promotions.topAdEnabled,
        topAdDays: promotions.topAdDays,
        topAdUntil: promotions.topAdUntil,

        isSponsored: promotions.topAdEnabled,
        sponsored: promotions.topAdEnabled,
        sponsoredDays: promotions.sponsoredDays,
        sponsoredUntil: promotions.sponsoredUntil,

        isHighlighted: promotions.highlight,
        highlighted: promotions.highlight,
        highlight: promotions.highlight,
        highlightDays: promotions.highlightDays,
        highlightUntil: promotions.highlightUntil,

        isUrgent: promotions.urgent,
        urgent: promotions.urgent,
        urgentDays: promotions.urgentDays,
        urgentUntil: promotions.urgentUntil,

        imageURLs,
        ownerUid: currentUser.uid,
        ownerId: currentUser.uid,
        ownerEmail: normEmail(currentUser.email),
        ownerName: currentUser.displayName || "",
        location: {
          city: normStr(form.locationText) || normStr(geo.city),
          country: geo.country || "",
          latitude: geo.latitude ?? null,
          longitude: geo.longitude ?? null,
        },
        status: postAsPendingReview ? "pending_review" : "open",
        moderationStatus: postAsPendingReview ? "pending_review" : "approved",
        moderationDecision,
        moderationMessage,
        moderationRoute,
        moderationCategoryRisk: categoryRisk,
        moderationSellerTrust: sellerTrust,
        moderationCheckedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        offerCount: 0,
        isAvailable: !postAsPendingReview,
      };

      const createdTradeId = await addTradeListing(newTrade);

      if (!CASH_ONLY_UI && ENABLE_SWAP_OFFER_INTENT_FLOW && isOfferFlow && createdTradeId) {
        // re-enable your TradeOffers creation logic later if swaps return
      }

      const sponsoredTotal = calcSponsoredTotal(promotions);

      if (sponsoredTotal > 0 && !postAsPendingReview) {
        setProgress((p) => ({ ...p, percent: 100, uploadedBytes: p.totalBytes }));
       
        showPopup({
          type: "success",
          title: "Trade posted",
          message: "Your trade has been posted. Redirecting to sponsored payment...",
        });

        try {
          setResumeTradeId(createdTradeId);
          redirectingToPayFast = true;
          await launchSponsoredPayment(createdTradeId);
          return;
        } catch (payErr) {
          redirectingToPayFast = false;
          console.error("Sponsored payment launch failed:", payErr);

        
          showPopup({
            type: "error",
            title: "Payment issue",
            message: friendlySponsoredPaymentError(payErr),
            autoHide: false,
          });
          return;
        }
      }

      setProgress((p) => ({ ...p, percent: 100, uploadedBytes: p.totalBytes }));
      clearPendingSponsoredPayment();

     const detailsHref = `/trade/${encodeURIComponent(createdTradeId)}`;

        if (postAsPendingReview) {
          showPopup({
            type: "success",
            title: "Trade submitted",
            message: "Your trade has been posted and is pending review before it goes live.",
            actionLabel: "Open trade",
            actionHref: detailsHref,
            autoHide: false,
          });
        } else {
          showPopup({
            type: "success",
            title: "Trade posted",
            message: "Your trade has been posted successfully.",
            actionLabel: "View trade",
            actionHref: detailsHref,
            autoHide: false,
          });
        }
    } catch (err) {
      console.error("Post trade failed:", err);

      const errMsg = String(err?.message || "").toLowerCase();
      const looksLikeSponsoredPaymentError =
        errMsg.includes("sponsored") ||
        errMsg.includes("payment session") ||
        errMsg.includes("paid promotions selected");

      showPopup({
        type: "error",
        title: "Could not post trade",
        message: looksLikeSponsoredPaymentError
          ? friendlySponsoredPaymentError(err)
          : friendlyError(err),
        autoHide: false,
      });
    } finally {
      if (!redirectingToPayFast) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>

      <div className="pt-wrap">
        <div className="pt-card">
          <div className="pt-topbar">
            <div className="pt-title">
              <div className="pt-titleMain">Post a Listing</div>

              {isOfferFlow && (
                <div className="offerFlowBanner">
                  <div>
                    You opened this from an offer flow for:{" "}
                    <strong>{requestedMeta.requestedTitle || offerIntent?.requestedTitle || "a trade"}</strong>
                  </div>
                  <div style={{ marginTop: 6, opacity: 0.85 }}>
                    Swaps are temporarily disabled. Post your listing normally, then make a cash offer from the listing page.
                  </div>

                  {(requestedMeta.requestedImage ||
                    requestedMeta.requestedCity ||
                    requestedMeta.requestedCountry) && (
                    <div
                      style={{
                        marginTop: 10,
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        flexWrap: "wrap",
                        padding: 10,
                        borderRadius: 12,
                        background: "rgba(255,255,255,0.55)",
                        border: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      {requestedMeta.requestedImage ? (
                        <img
                          src={requestedMeta.requestedImage}
                          alt="Requested trade"
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 10,
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 10,
                            background: "rgba(0,0,0,0.08)",
                            flexShrink: 0,
                          }}
                        />
                      )}

                      <div style={{ lineHeight: 1.2 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>
                          {requestedMeta.requestedTitle || offerIntent?.requestedTitle || "Target trade"}
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.8 }}>
                          {requestedMeta.requestedCity || "—"}
                          {requestedMeta.requestedCountry ? `, ${requestedMeta.requestedCountry}` : ""}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-titleSub">Create a cash listing.</div>
              {resumeTradeId ? (
                <div className="pt-hint" style={{ marginTop: 6 }}>
                  Pending sponsored payment linked to trade: {resumeTradeId}
                </div>
              ) : null}
            </div>

            <button type="button" className="pt-iconBtn" onClick={close} aria-label="Close">
              <FaTimes />
            </button>
          </div>

          {isSubmitting ? (
            <div className="pt-alert" style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontWeight: 700 }}>Uploading images…</div>
                <div style={{ fontVariantNumeric: "tabular-nums" }}>{progress.percent}%</div>
              </div>
              <div
                style={{
                  height: 10,
                  background: "rgba(0,0,0,0.08)",
                  borderRadius: 999,
                  overflow: "hidden",
                  marginTop: 8,
                }}
              >
                <div
                  style={{
                    width: `${progress.percent}%`,
                    height: "100%",
                    background: "rgba(0,0,0,0.55)",
                  }}
                />
              </div>
              <div className="pt-hint" style={{ marginTop: 8 }}>
                {Math.round(progress.uploadedBytes / (1024 * 1024))}MB /{" "}
                {Math.round(progress.totalBytes / (1024 * 1024))}MB
              </div>
            </div>
          ) : null}

          <form key={forceRenderKey} className="pt-form" onSubmit={handleSubmit}>
            <div className="pt-requirementsPanel">
              <div className="pt-requirementsHead">
                <div>
                  <div className="pt-requirementsTitle">Before you post</div>
                  <div className="pt-requirementsSub">
                    Complete the required sections below. The post button will activate once everything is ready.
                  </div>
                </div>

                <div className={`pt-requirementsScore ${canSubmit ? "is-ready" : ""}`}>
                  {validation.items.length - validation.missing.length}/{validation.items.length} ready
                </div>
              </div>

              <div className="pt-requirementsGrid">
                {validation.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`pt-requirementChip ${
                      item.done ? "is-done" : attemptedSubmit ? "is-missing" : ""
                    }`}
                    onClick={() => {
                      const refMap = {
                        title: titleRef,
                        category: categoryRef,
                        description: descriptionRef,
                        condition: conditionRef,
                        location: locationRef,
                        price: priceRef,
                        images: imagesRef,
                      };
                      refMap[item.refKey]?.current?.scrollIntoView?.({
                        behavior: "smooth",
                        block: "center",
                      });
                    }}
                  >
                    <span>{item.done ? "✓" : "•"}</span>
                    <strong>{item.label}</strong>
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-grid2">
              <div className={`pt-field ${fieldIssueClass("title")}`} ref={titleRef}>
                <label>Item name</label>
                <input
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="e.g., Laptop, Phone, Sofa"
                  required
                />
                {renderRequirementHint("title")}
              </div>

              <div className={`pt-field ${fieldIssueClass("category")}`} ref={categoryRef}>
                <label>Category</label>
                <select value={form.category} onChange={(e) => setField("category", e.target.value)} required>
                  <option value="">Select</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {renderRequirementHint("category")}
              </div>
            </div>

            <div className={`pt-field ${fieldIssueClass("description")}`} ref={descriptionRef}>
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Describe the item, included accessories, defects, and collection/delivery notes."
                rows={4}
                required
              />
              {renderRequirementHint("description")}
            </div>

            <div className="pt-grid2">
              <div className={`pt-field ${fieldIssueClass("condition")}`} ref={conditionRef}>
                <label>Condition</label>
                <select value={form.condition} onChange={(e) => setField("condition", e.target.value)} required>
                  <option value="">Select</option>
                  {CONDITIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {renderRequirementHint("condition")}
              </div>

              <div className={`pt-field ${fieldIssueClass("location")}`} ref={locationRef}>
                <label>
                  Location <span className="pt-muted">(city/suburb)</span>
                </label>

                <div className="pt-locationRow" style={{ gap: 10 }}>
                  <FaMapMarkerAlt className="pt-locIcon" />

                  <input
                    value={form.locationText}
                    onChange={(e) => setField("locationText", e.target.value)}
                    placeholder="e.g., Johannesburg"
                    style={{ flex: 1 }}
                  />

                  <button
                    type="button"
                    className="pt-btn ghost"
                    onClick={requestLocation}
                    disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Use my location
                  </button>
                </div>

                <div className="pt-hint">
                  Detected: {geo.city || "—"}, {geo.country || "—"}
                  {geo.latitude != null && geo.longitude != null
                    ? ` (${geo.latitude.toFixed(2)}, ${geo.longitude.toFixed(2)})`
                    : ""}
                </div>

                {locError ? (
                  <div className="pt-hint" style={{ color: "crimson" }}>
                    {locError}
                  </div>
                ) : null}

                {renderRequirementHint("location")}
              </div>
            </div>

            <div className="pt-section">
              <div className="pt-sectionHead">Price</div>
              <div className={`pt-field ${fieldIssueClass("price")}`} style={{ marginTop: 8 }} ref={priceRef}>
                <label>Price (ZAR)</label>
                <input
                  inputMode="numeric"
                  value={form.expectedCashAmount}
                  onChange={(e) => setField("expectedCashAmount", e.target.value)}
                  placeholder="e.g., 12000"
                  required
                />
                <div className="pt-hint">Cash listings only.</div>
                {renderRequirementHint("price")}
              </div>

              <div className="pt-field" style={{ marginTop: 16 }}>
                <label>Minimum Amount (ZAR) - Optional</label>
                <input
                  inputMode="numeric"
                  value={form.minimumCashAmount}
                  onChange={(e) => setField("minimumCashAmount", e.target.value)}
                  placeholder="e.g., 10000"
                />
                <div className="pt-hint">The lowest amount you'll accept for this trade.</div>
              </div>
            </div>

            <div className={`pt-section ${fieldIssueClass("images")}`} ref={imagesRef}>
              <div className="pt-sectionHead">Images</div>

              <div className="pt-uploadGuide">
                <strong>Image requirements</strong>
                <ul>
                  <li>Upload at least 1 clear image.</li>
                  <li>Maximum {MAX_IMAGES} images.</li>
                  <li>Accepted formats: JPG, PNG, or WebP.</li>
                  <li>Maximum {MAX_MB_PER_IMAGE}MB per image.</li>
                </ul>
              </div>

              <div className="pt-uploadRow">
                <label className="pt-uploadBtn">
                  <FaCloudUploadAlt />
                  <span>Upload images</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                    disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy}
                  />
                </label>

                <div className="pt-hint">
                  {imageFiles.length ? `${imageFiles.length} selected` : `Up to ${MAX_IMAGES} images`}
                </div>
              </div>

              {renderRequirementHint("images")}

              {previewImages.length > 0 ? (
                <div className="pt-previews">
                  {previewImages.map((src, idx) => (
                    <img key={idx} src={src} alt={`Preview ${idx + 1}`} className="pt-previewImg" />
                  ))}
                </div>
              ) : null}
            </div>

            <div className="pt-section">
              <div className="pt-sectionHead">Promote my ad</div>

              <div className="pt-grid2">
                <div className="pt-miniCard">
                  <div className="pt-miniTitle">Top Ad</div>
                  <div className="pt-miniBody">
                    <label className="pt-radio">
                      <input
                        type="radio"
                        name="topAd"
                        value="7"
                        checked={form.promotions.topAd === "7"}
                        onChange={() => setPromo("topAd", "7")}
                        disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy}
                      />
                      <span>7 days – R30</span>
                    </label>

                    <label className="pt-radio">
                      <input
                        type="radio"
                        name="topAd"
                        value="15"
                        checked={form.promotions.topAd === "15"}
                        onChange={() => setPromo("topAd", "15")}
                        disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy}
                      />
                      <span>15 days – R70</span>
                    </label>

                    <label className="pt-radio">
                      <input
                        type="radio"
                        name="topAd"
                        value="31"
                        checked={form.promotions.topAd === "31"}
                        onChange={() => setPromo("topAd", "31")}
                        disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy}
                      />
                      <span>
                        31 days – R90 <span className="pt-pill">Best value</span>
                      </span>
                    </label>

                    <label className="pt-radio">
                      <input
                        type="radio"
                        name="topAd"
                        value=""
                        checked={!form.promotions.topAd}
                        onChange={() => setPromo("topAd", null)}
                        disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy}
                      />
                      <span>No Top Ad</span>
                    </label>
                  </div>
                </div>

                <div className="pt-miniCard">
                  <div className="pt-miniTitle">Extras <span className="pt-muted">(add-ons to Top Ad)</span></div>
                  <div className="pt-miniBody">
                    <label className="pt-check">
                      <input
                        type="checkbox"
                        checked={!!form.promotions.highlight}
                        onChange={(e) => setPromo("highlight", e.target.checked)}
                        disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy || !form.promotions.topAd}
                      />
                      <span>Highlight – 21 days – R70 {!form.promotions.topAd ? "(requires Top Ad)" : ""}</span>
                    </label>

                    <label className="pt-check">
                      <input
                        type="checkbox"
                        checked={!!form.promotions.urgent}
                        onChange={(e) => setPromo("urgent", e.target.checked)}
                        disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy || !form.promotions.topAd}
                      />
                      <span>Urgent – 7 days – R25 {!form.promotions.topAd ? "(requires Top Ad)" : ""}</span>
                    </label>

                    <label className="pt-check" style={{ marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={form.promotions.topAd === "31" && form.promotions.highlight && form.promotions.urgent}
                        onChange={(e) => handleSelectAllPromos(e.target.checked)}
                        disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy}
                      />
                      <span>Select all (31 days Top Ad + 21-day Highlight + Urgent)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {!canSubmit ? (
              <div className={`pt-submitHelp ${attemptedSubmit ? "is-active" : ""}`}>
                <strong>Post listing is not ready yet.</strong>
                <span>
                  {validation.firstMissing?.message ||
                    "Complete the required sections above to activate the button."}
                </span>
              </div>
            ) : (
              <div className="pt-submitHelp is-ready">
                <strong>Ready to post.</strong>
                <span>Your listing has the required information.</span>
              </div>
            )}

            <div className="pt-actions">
              <button
                type="submit"
                className="pt-btn primary"
                disabled={!canSubmit || isSubmitting || moderationBusy || sponsoredPaymentBusy}
                title={!canSubmit ? validation.firstMissing?.message || "Complete required fields first." : ""}
              >
                {isSubmitting
                  ? `Posting… ${progress.percent}%`
                  : moderationBusy
                  ? "Checking content…"
                  : sponsoredPaymentBusy
                  ? "Opening payment…"
                  : "Post listing"}
              </button>

              <button
                type="button"
                className="pt-btn ghost"
                onClick={close}
                disabled={isSubmitting || moderationBusy || sponsoredPaymentBusy}
              >
                Cancel
              </button>
            </div>

            <div className="pt-footnote">
              By posting, you agree to platform safety rules (no off-platform contact or payments).
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddTrade;